import type { SurveyAnswerRecord, SurveyConfig, SurveyElementJson } from '@coorum-research/shared-common/types'
import type { SurveyModel } from 'survey-core'
import { useMemo, useState } from 'react'
import { SurveyElementForm } from './survey-element-form'

export function getElementByPageIndex(
  pageIndex: number,
  allElements: SurveyElementJson[],
): SurveyElementJson {
  return allElements[pageIndex]
}

/**
 * getCurrentSurveyDataは、回答した全データ。
 * その中から、１回答分のデータを取得する。
 * 回答データには
 * {
 *   "01JD1J222CB8PJNYJN6SV28GKS": "01J874DK9H99E5S7980XS2CRX7",
 * }
 * のパターンだけでなく
 * {
 *   "01JD1J222CB8PJNYJN6SV28GKS": "other",
 *   "01JD1J222CB8PJNYJN6SV28GKS-Comment": "その他の回答です"
 * }
 * のようにコメントを入れた場合もあるので、Recordのkeyが1つでは限らない。
 *
 */
export function getElementValueByPageIndex(
  currentElement: SurveyElementJson,
  allElementRecord: SurveyAnswerRecord | null,
): SurveyAnswerRecord | null {
  if (!allElementRecord) {
    return null
  }

  const record: SurveyAnswerRecord = {}

  // パネル要素の場合、子要素の回答データも含める
  if (currentElement.type === 'panel' && currentElement.elements) {
    // パネル自体の回答データを追加
    if (allElementRecord[currentElement.name]) {
      record[currentElement.name] = allElementRecord[currentElement.name]
    }
    if (allElementRecord[`${currentElement.name}-Comment`]) {
      record[`${currentElement.name}-Comment`] = allElementRecord[`${currentElement.name}-Comment`]
    }

    // 子要素の回答データを追加
    currentElement.elements.forEach((element) => {
      if (allElementRecord[element.name]) {
        record[element.name] = allElementRecord[element.name]
      }
      if (allElementRecord[`${element.name}-Comment`]) {
        record[`${element.name}-Comment`] = allElementRecord[`${element.name}-Comment`]
      }
    })
  } else {
    // 通常の要素の場合
    if (allElementRecord[currentElement.name] !== undefined) {
      record[currentElement.name] = allElementRecord[currentElement.name]
    }
    if (allElementRecord[`${currentElement.name}-Comment`]) {
      record[`${currentElement.name}-Comment`] = allElementRecord[`${currentElement.name}-Comment`]
    }
  }

  return Object.keys(record).length > 0 ? record : null
}

export function useSurveyElementForm({
  currentElementJson,
  currentElementResultRecord,
  getModel,
}: {
  currentElementJson: SurveyElementJson | null
  currentElementResultRecord: SurveyAnswerRecord | null
  getModel: (perElementSurveyConfig: SurveyConfig, perElementResultData: SurveyAnswerRecord | null) => SurveyModel
}) {
  // NOTE: validation messageも、form classに入れたかったけど、reactiveにするのが難しいのでこのまま使う。
  const [validationError, setValidationError] = useState<string>('')

  // 現在の設問の設定データ、回答済みデータを元に、survey form modelを生成
  const currentModel = useMemo(() => {
    if (!currentElementJson) {
      return null
    }

    const currentElementSurveyConfig: SurveyConfig = {
      elements: currentElementJson ? [currentElementJson] : [],
    }

    return getModel(currentElementSurveyConfig, currentElementResultRecord)
  }, [currentElementJson, currentElementResultRecord, getModel])

  const currentForm = useMemo(() => {
    if (!currentElementJson) {
      return null
    }

    return new SurveyElementForm(
      currentElementJson,
      currentElementResultRecord,
      currentModel,
    )
  }, [currentElementJson, currentElementResultRecord, currentModel])

  return {
    currentForm,
    validationError,
    setValidationError,
  }
}
